import React, {useEffect, useRef, useState} from 'react';
import {Layout, Pagination, Alert} from 'antd';
import {useNavigate, useLocation} from 'react-router-dom';
import CustomCard from '../CustomCard/CustomCard.jsx';
import DocumentSidebar from '../DocumentSidebar/DocumentSidebar.jsx';
import queryString from 'query-string';
import {track} from '../../services/documentService.js';
import {createErrorElement} from '../../utils/utils.js';
import {useReload} from '../../utils/ReloadProvider.jsx';
import {useUserContext} from '../../utils/UserProvider.jsx';
import {userGroupsPostFix} from '../../utils/commons.js';

// Custom hooks
import {useDocumentFilters} from '../../hooks/useDocumentFilters.js';
import {usePagination} from '../../hooks/usePagination.js';
import {useDocumentData} from '../../hooks/useDocumentData.js';
import {useFilterOptions} from '../../hooks/useFilterOptions.js';

const {Sider, Content} = Layout;

const MainPage = ({accessToken}) => {
  const {reloadKey} = useReload();
  const {userGroups, userName} = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  // State for loading states
  const [isLoadingFreeSearch, setIsLoadingFreeSearch] = useState(false);
  const [mainError, setMainError] = useState(null);

  // Refs for tracking
  const prevReloadKeyRef = useRef(null);
  const isFirstRender = useRef(true);
  const lastTrackedState = useRef(null);

  // Custom hooks
  const {
    filters,
    searchValue,
    setFilters,
    updateFilters,
    clearFilters,
    updateSearchValue,
    getFiltersFromQuery,
    buildQueryString,
  } = useDocumentFilters({});

  const {
    page,
    pageSize,
    totalDocuments,
    setTotalDocuments,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
  } = usePagination(1, 20);

  const {
    documents,
    isLoading: isLoadingDocuments,
    error: documentError,
    setError: setDocumentError,
    loadDocuments,
    clearCache,
  } = useDocumentData(accessToken);

  const {
    options,
    isLoading: isLoadingOptions,
    error: optionsError,
    setError: setOptionsError,
    fetchAllOptions,
    updateSpecificOptions,
  } = useFilterOptions(accessToken);

  // Derived values
  const normalizedSearch = queryString.stringify(
    queryString.parse(location?.search)
  );

  const showModeratorButtonForExternalUser = userGroups?.some(group =>
    group.includes(userGroupsPostFix.ExternalModerator)
  );

  const showModeratorButtons = userGroups?.some(group =>
    group.includes(userGroupsPostFix.Moderator)
  );
  // Process documents to ensure unique keys
  const processedDocuments = React.useMemo(() => {
    if (!Array.isArray(documents)) return documents;

    return documents.map((doc, index) => {
      // Create a unique key using multiple fallback strategies
      const uniqueKey =
        doc?.id || doc?.documentId || doc?.name || `doc-${index}-${Date.now()}`;

      return {
        ...doc,
        uniqueKey,
      };
    });
  }, [documents]);

  // Initialize from URL parameters
  useEffect(() => {
    const parsedQuery = queryString.parse(normalizedSearch);
    const initialFilters = getFiltersFromQuery(parsedQuery);

    setFilters(initialFilters);
    updateSearchValue(initialFilters.freeSearch);

    const newPage = parsedQuery.page ? parseInt(parsedQuery.page, 10) : 1;
    const newPageSize = parsedQuery.pageSize
      ? parseInt(parsedQuery.pageSize, 10)
      : 20;

    handlePageChange(newPage, newPageSize);

    const wasReloadKeyTriggered = prevReloadKeyRef.current !== reloadKey;

    // Load initial data
    Promise.all([
      fetchAllOptions(initialFilters),
      loadDocuments(
        initialFilters,
        newPage,
        newPageSize,
        parsedQuery,
        wasReloadKeyTriggered
      ),
    ])
      .then(([, documentResult]) => {
        if (documentResult) {
          setTotalDocuments(documentResult.totalCount);
        }
      })
      .catch(error => {
        setMainError(createErrorElement(error, 'initial data load'));
      });

    prevReloadKeyRef.current = reloadKey;
  }, [normalizedSearch, reloadKey]);

  // Handle filter changes
  const handleFilterChange = async (key, value) => {
    if (key === 'freeSearch') {
      setIsLoadingFreeSearch(true);
    }

    updateFilters(key, value, async updatedFilters => {
      try {
        clearCache();
        resetPagination();

        const newQueryString = buildQueryString(updatedFilters, 1, pageSize);
        navigate(
          {
            pathname: location?.pathname,
            search: `?${newQueryString}`,
          },
          {replace: true}
        );

        const [, documentResult] = await Promise.all([
          updateSpecificOptions(updatedFilters, key, options),
          loadDocuments(updatedFilters, 1, pageSize, newQueryString),
        ]);

        if (documentResult) {
          setTotalDocuments(documentResult.totalCount);
        }

        if (key === 'freeSearch') {
          setIsLoadingFreeSearch(false);
        }
      } catch (error) {
        setMainError(createErrorElement(error, 'filter change'));
        if (key === 'freeSearch') {
          setIsLoadingFreeSearch(false);
        }
      }
    });
  };

  // Handle page navigation
  const onPageChange = async (newPage, newPageSize) => {
    try {
      handlePageChange(newPage, newPageSize);

      const queryStringified = buildQueryString(filters, newPage, newPageSize);
      navigate(
        {
          pathname: location?.pathname,
          search: `?${queryStringified}`,
        },
        {replace: true}
      );

      const result = await loadDocuments(
        filters,
        newPage,
        newPageSize,
        queryStringified
      );
      if (result) {
        setTotalDocuments(result.totalCount);
      }
    } catch (error) {
      setMainError(createErrorElement(error, 'page change'));
    }
  };

  // Handle page size change
  const onPageSizeChange = async (current, newPageSize) => {
    try {
      clearCache();
      handlePageSizeChange(current, newPageSize);

      const queryStringified = buildQueryString(filters, 1, newPageSize);
      navigate(
        {
          pathname: location?.pathname,
          search: `?${queryStringified}`,
        },
        {replace: true}
      );

      const result = await loadDocuments(
        filters,
        1,
        newPageSize,
        queryStringified,
        true
      );
      if (result) {
        setTotalDocuments(result.totalCount);
      }
    } catch (error) {
      setMainError(createErrorElement(error, 'page size change'));
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    clearFilters(pageSize, async clearedFilters => {
      try {
        clearCache();
        const result = await loadDocuments(
          clearedFilters,
          1,
          pageSize,
          '',
          true
        );
        if (result) {
          setTotalDocuments(result.totalCount);
        }
        await fetchAllOptions(clearedFilters);
      } catch (error) {
        setMainError(createErrorElement(error, 'clear filters'));
      }
    });
  };

  // Tracking effect
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const currentState = JSON.stringify({filters, page, pageSize});

    if (lastTrackedState.current === currentState) {
      return;
    }

    lastTrackedState.current = currentState;

    if (!accessToken || !userName) return;

    const allEmpty = Object.values(filters).every(
      arr => Array.isArray(arr) && arr.length === 0
    );

    if (!allEmpty) {
      track(page, pageSize, filters, accessToken, userName, userGroups).catch(
        err => console.error('Error tracking user action:', err)
      );
    }
  }, [filters, page, pageSize, accessToken, userName, userGroups]);

  // Moderator panel handlers
  const handleOpenModeratorPanel = () =>
    window.open('/moderatePanel', '_blank');
  const handleOpenUsageStatistic = () =>
    window.open('/moderatePanel/usageStatistic', '_blank');
  const handleOpenAudit = () => window.open('/moderatePanel/audit', '_blank');

  // Combine all errors
  const combinedError = mainError || documentError || optionsError;
  const isLoading = isLoadingDocuments || isLoadingOptions;

  return (
    <Layout style={{}}>
      {isLoading && <div className="loading">Loading&#8230;</div>}

      <Sider width={320} breakpoint="lg" collapsedWidth="0">
        <DocumentSidebar
          options={options}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          onFreeSearch={value => handleFilterChange('freeSearch', value)}
          searchValue={searchValue}
          onSearchValueChange={updateSearchValue}
          isLoadingFreeSearch={isLoadingFreeSearch}
          totalDocuments={totalDocuments}
          showModeratorButtons={showModeratorButtons}
          showModeratorButtonForExternalUser={
            showModeratorButtonForExternalUser
          }
          onOpenModeratorPanel={handleOpenModeratorPanel}
          onOpenUsageStatistic={handleOpenUsageStatistic}
          onOpenAudit={handleOpenAudit}
        />
      </Sider>

      <Content
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
          padding: 0,
          margin: 0,
        }}
      >
        {combinedError && (
          <div>
            <Alert
              message={combinedError}
              type="error"
              showIcon
              style={{margin: '15px'}}
            />
          </div>
        )}

        <div style={{flex: 1, overflowY: 'auto'}}>
          <CustomCard
            documents={processedDocuments}
            isLoading={isLoadingDocuments}
          />
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'white',
            padding: '16px',
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Pagination
            current={page}
            pageSize={pageSize}
            pageSizeOptions={['10', '20', '50']}
            total={totalDocuments}
            onChange={onPageChange}
            onShowSizeChange={onPageSizeChange}
            showSizeChanger
          />
        </div>
      </Content>
    </Layout>
  );
};

export default MainPage;
